import React, { useEffect, useState, useCallback } from "react";
import * as S from "./AdminNotices.styled";
import { noticeService, NoticeItem, NoticeType } from "services/noticeService";
import AdminNoticeDetail from "./AdminNoticeDetail";

const PAGE_SIZE = 15;

const AdminNotices: React.FC = () => {
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [types, setTypes] = useState<NoticeType[]>([]);
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedNoticeId, setSelectedNoticeId] = useState<number | null>(null);

  useEffect(() => {
    noticeService.getNoticeTypes().then(setTypes).catch(() => {});
  }, []);

  const fetchNotices = useCallback((p: number, type: string) => {
    const universityId = Number(localStorage.getItem("adminUniversityId"));
    setIsLoading(true);
    noticeService
      .getNotices(universityId, p, PAGE_SIZE, 'createdAt,Desc', type || undefined)
      .then((res) => {
        setNotices(res.content);
        setTotalPages(res.totalPages);
        setTotalElements(res.totalElements);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchNotices(page, selectedType);
  }, [fetchNotices, page, selectedType]);

  const filtered = notices.filter((n) =>
    n.title.toLowerCase().includes(query.toLowerCase())
  );

  const typeMap = Object.fromEntries(types.map((t) => [t.name, t.displayName]));

  const handlePageChange = (next: number) => {
    setPage(next);
    setQuery("");
  };

  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    setPage(0);
    setQuery("");
  };

  /* 페이지 번호 목록 생성 (최대 5개) */
  const pageNumbers = () => {
    const total = totalPages;
    if (total <= 5) return Array.from({ length: total }, (_, i) => i);
    const start = Math.max(0, Math.min(page - 2, total - 5));
    return Array.from({ length: 5 }, (_, i) => start + i);
  };

  return (
    <>
      <S.PageHeader>
        <S.PageTitle>공지사항 목록</S.PageTitle>
      </S.PageHeader>

      <S.Toolbar>
        <S.SearchWrap>
          <S.SearchIcon>🔍</S.SearchIcon>
          <S.SearchInput
            placeholder="제목으로 검색..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </S.SearchWrap>
        <S.TypeSelect
          value={selectedType}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          <option value="">전체 분류</option>
          {types.map((t) => (
            <option key={t.name} value={t.name}>{t.displayName}</option>
          ))}
        </S.TypeSelect>
        <S.CountText>
          {isLoading ? "로딩 중..." : `총 ${totalElements}개 공지`}
        </S.CountText>
      </S.Toolbar>

      <S.Card>
        <S.Table>
          <S.Thead>
            <tr>
              <S.Th width="56px">#</S.Th>
              <S.Th width="100px">분류</S.Th>
              <S.Th>제목</S.Th>
              <S.Th width="140px">날짜</S.Th>
            </tr>
          </S.Thead>
          <S.Tbody>
            {!isLoading && filtered.length === 0 ? (
              <tr>
                <S.EmptyCell colSpan={4}>
                  {query
                    ? `"${query}"에 해당하는 공지가 없습니다.`
                    : "등록된 공지사항이 없습니다."}
                </S.EmptyCell>
              </tr>
            ) : (
              filtered.map((notice, index) => (
                <S.Tr
                  key={notice.id}
                  onClick={() => setSelectedNoticeId(notice.id)}
                >
                  <S.Td>
                    <S.IndexCell>{page * PAGE_SIZE + index + 1}</S.IndexCell>
                  </S.Td>
                  <S.Td>
                    {notice.type && (
                      <S.TypeBadge type={notice.type}>
                        {typeMap[notice.type] ?? notice.type}
                      </S.TypeBadge>
                    )}
                  </S.Td>
                  <S.Td>
                    <S.NoticeTitle>{notice.title}</S.NoticeTitle>
                  </S.Td>
                  <S.Td>
                    <S.DateText>{notice.date}</S.DateText>
                  </S.Td>
                </S.Tr>
              ))
            )}
          </S.Tbody>
        </S.Table>

        {!isLoading && totalPages > 1 && (
          <S.Pagination>
            <S.PageButton
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 0}
            >
              ‹
            </S.PageButton>
            {pageNumbers().map((p) => (
              <S.PageButton
                key={p}
                active={p === page}
                onClick={() => handlePageChange(p)}
              >
                {p + 1}
              </S.PageButton>
            ))}
            <S.PageButton
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages - 1}
            >
              ›
            </S.PageButton>
          </S.Pagination>
        )}
      </S.Card>

      {selectedNoticeId !== null && (
        <AdminNoticeDetail
          noticeId={selectedNoticeId}
          onClose={() => setSelectedNoticeId(null)}
          types={types}
          onDeleted={() => {
            setSelectedNoticeId(null);
            fetchNotices(page, selectedType);
          }}
          onUpdated={() => fetchNotices(page, selectedType)}
        />
      )}
    </>
  );
};

export default AdminNotices;
